import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Card, Row, Col, Input, Button, AutoComplete, Space, Spin, Alert, Tag, Tabs, Table,
  Statistic, Tooltip, Empty, Descriptions, Divider, message,
} from 'antd';
import {
  SendOutlined, ReloadOutlined, SwapOutlined, ToolOutlined,
  BulbOutlined, CaretRightOutlined, CheckCircleFilled, CloseCircleFilled,
  LoadingOutlined, StopOutlined, PaperClipOutlined, InboxOutlined, DeleteOutlined,
} from '@ant-design/icons';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../libs/api';

const { TextArea } = Input;

/**
 * What the platform will read from an attachment.
 *
 * parse_electricity_bill takes a PDF or an image, so those are what the picker
 * offers and what the server accepts. Kept in step with UPLOAD_TYPES in the
 * controller — the server is the check, this is only to save somebody choosing
 * a file that is going to be refused.
 */
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.heic,.heif';
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

const fmtBytes = (n) => {
  if (n == null) return '';
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};

/**
 * A figure the platform holds, or an honest gap.
 *
 * np_system has real holes — pv_kw and dnsp_code come back null for sites whose
 * battery and inverter are both recorded — so a blank cell would read as "this
 * panel is broken" when it means "nobody has captured this yet".
 */
const NOT_RECORDED = <span style={{ color: '#bbb' }}>not recorded</span>;
const num = (v, unit) => (v == null || v === '' ? NOT_RECORDED : (
  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
    {typeof v === 'number' ? v : String(v)}
    {unit ? <span style={{ color: '#888', fontSize: 11 }}> {unit}</span> : null}
  </span>
));
const orGap = (v) => (v == null || v === '' ? NOT_RECORDED : String(v));

/** First key that is actually present — the tariff blocks are not one fixed shape. */
const pick = (obj, keys) => {
  for (const k of keys) {
    if (obj && obj[k] != null && obj[k] !== '') return obj[k];
  }
  return null;
};

/**
 * Whatever scalars a tariff object carries, in the order it carries them.
 *
 * No bundle exists for any SN yet, so the populated shape of a linked tariff has
 * never been seen from here. Naming the fields would be guessing; this shows
 * what is there, so a real tariff is legible the first time one is linked
 * instead of rendering as a row of blanks.
 */
const scalarRows = (obj) => Object.entries(obj || {})
  .filter(([, v]) => v != null && v !== '' && typeof v !== 'object')
  .slice(0, 14);

// Everything reaches the assistant through our own backend: that service is
// IAM-gated to this app's service account, a credential a browser cannot hold.

// Both columns are pinned to the same height so the chat and the debug stack
// line up; the chart card takes a fixed slice and the tab card the remainder.
const PANEL_H = 660;
const CHART_CARD_H = 300;
const GAP = 12;

// The assistant replies in markdown — bold, headings, bullet lists and pipe
// tables all appear in normal output. Rendered through react-markdown rather
// than injected as HTML: this is model output, so it must never reach
// dangerouslySetInnerHTML.
const MD_COMPONENTS = {
  p: ({ children }) => <div style={{ margin: '0 0 6px' }}>{children}</div>,
  h1: ({ children }) => <div style={{ fontWeight: 700, fontSize: 15, margin: '10px 0 4px' }}>{children}</div>,
  h2: ({ children }) => <div style={{ fontWeight: 700, fontSize: 14, margin: '10px 0 4px' }}>{children}</div>,
  h3: ({ children }) => <div style={{ fontWeight: 700, fontSize: 13, margin: '8px 0 4px' }}>{children}</div>,
  ul: ({ children }) => <ul style={{ margin: '0 0 6px', paddingLeft: 18 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '0 0 6px', paddingLeft: 18 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
  code: ({ inline, children }) => inline
    ? <code style={{ background: '#eef1f5', padding: '1px 4px', borderRadius: 3, fontSize: 12 }}>{children}</code>
    : <pre style={{ background: '#0b1020', color: '#e5e7eb', padding: 8, borderRadius: 4, fontSize: 11, overflow: 'auto', margin: '0 0 6px' }}>
        <code>{children}</code>
      </pre>,
  hr: () => <div style={{ borderTop: '1px solid #e5e7eb', margin: '8px 0' }} />,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noreferrer">{children}</a>,
  // Wide pipe tables scroll inside the bubble rather than stretching the column
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', margin: '0 0 8px' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>{children}</table>
    </div>
  ),
  th: ({ children }) => <th style={{ border: '1px solid #e5e7eb', padding: '3px 6px', background: '#f5f7fa', textAlign: 'left' }}>{children}</th>,
  td: ({ children }) => <td style={{ border: '1px solid #e5e7eb', padding: '3px 6px' }}>{children}</td>,
  blockquote: ({ children }) => <div style={{ borderLeft: '3px solid #d9d9d9', paddingLeft: 8, color: '#666' }}>{children}</div>,
};

const Markdown = ({ children }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
    {String(children || '')}
  </ReactMarkdown>
);

// The plan is what a caller picks now; the platform's router picks the model per
// turn. Only savvy is live — the rest answer 501 — so they are shown as
// unavailable rather than hidden, since the platform intends to build them.
const PLAN_LABEL = { savvy: 'Savvy', user: 'User', group: 'Group', asset: 'Asset' };
// The plan this page uses, fixed rather than chosen. `user` is the plan whose
// tool whitelist excludes write_dispatch_command, so pinning it here is what
// keeps a chat on a management dashboard from being able to change what a
// battery does. Overridable only server-side, via VPP_AI_PLAN.
const PLAN = 'user';

// The router's tier is the only remaining visibility into model choice, so it is
// surfaced per turn rather than buried in the debug panel.
const TIER_COLOR = { low: 'green', high: 'volcano' };
const TIER_HINT = {
  low: 'Router picked the cheap model for this turn',
  high: 'Router escalated this turn — long or analytical prompt, or 3+ tool calls in this session',
};

const fmtInt = (n) => (n == null ? '-' : Number(n).toLocaleString());
const fmtMs = (ms) => (ms == null ? '-' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);
// Only AUD is shown. A turn runs from a fraction of a cent to a few dollars, so
// the precision follows the magnitude rather than rounding cheap turns to $0.00.
const fmtAud = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '-';
  const n = Number(v);
  if (n === 0) return 'A$0';
  if (n < 0.01) return `A$${n.toFixed(4)}`;
  if (n < 1) return `A$${n.toFixed(3)}`;
  return `A$${n.toFixed(2)}`;
};
const clockOf = (ts) => (ts ? new Date(ts).toLocaleTimeString('en-AU', { hour12: false }) : '-');

/**
 * Tool results carry timeseries under a handful of different shapes depending on
 * which tool produced them, so the chart looks for series rather than assuming
 * one schema. Returns null when a result holds nothing plottable, which keeps
 * the panel silent instead of drawing an empty axis.
 */
// Tool results carry their plottable data under whatever key suits the tool:
// simulate_strategy returns `daily` and `bands`, forecasts return `timeseries`,
// others use `hourly` or `intervals`. Matching on a list of known key names meant
// a tool naming its array anything else rendered as raw JSON — which is what
// simulate_strategy did.
//
// So the scan is structural instead: walk the result, collect every array that
// looks chartable, and rank them. A new tool with a new key name works with no
// change here.

const TIME_KEY_EXACT = /^(t|ts|time|timestamp|interval|dt|hour|period|x|date|day|datetime)$/i;
const TIME_KEY_LOOSE = /(time|date|hour|day|period|interval)/i;
const LABEL_KEY = /^(label|name|category|key|band|tier|bucket|type|id|slot)$/i;

// A numeric string still plots; recharts just needs a real number.
const asNumber = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
};

const isPlainRow = (v) => v && typeof v === 'object' && !Array.isArray(v);

/** Describe one array as a chartable series, or null if it is not one. */
function describeArray(arr, path) {
  if (!Array.isArray(arr) || arr.length < 2 || !isPlainRow(arr[0])) return null;

  // Union the keys of the first few rows: the first row can be missing a field.
  const keys = [];
  for (const row of arr.slice(0, 8)) {
    if (!isPlainRow(row)) return null;
    for (const k of Object.keys(row)) if (!keys.includes(k)) keys.push(k);
  }

  const numericKeys = keys.filter((k) => arr.some((r) => asNumber(r[k]) !== null));
  if (!numericKeys.length) return null;

  const timeKey = keys.find((k) => TIME_KEY_EXACT.test(k) && !numericKeys.includes(k))
    || keys.find((k) => TIME_KEY_EXACT.test(k))
    || keys.find((k) => TIME_KEY_LOOSE.test(k));

  // Failing a time axis, a string column makes this a categorical chart —
  // bands / tiers / buckets are worth drawing as bars, not hiding as JSON.
  const labelKey = timeKey
    || keys.find((k) => LABEL_KEY.test(k) && typeof arr[0][k] === 'string')
    || keys.find((k) => typeof arr[0][k] === 'string');
  if (!labelKey) return null;

  const valueKeys = numericKeys.filter((k) => k !== labelKey);
  if (!valueKeys.length) return null;

  const kind = timeKey ? 'time' : 'category';
  return {
    kind,
    path,
    labelKey,
    // Keep the widest-varying columns: a field that never moves adds a legend
    // entry and a flat line, and crowds out the ones that matter.
    seriesKeys: pickSeriesKeys(arr, valueKeys),
    rows: arr.slice(0, 400).map((r) => {
      const out = { __label: shortLabel(r[labelKey], labelKey), __full: fullLabel(r[labelKey]) };
      for (const k of valueKeys) {
        const n = asNumber(r[k]);
        if (n !== null) out[k] = n;
      }
      return out;
    }),
    // time series beat categories, then longer beats shorter, then wider
    score: (kind === 'time' ? 10000 : 0) + Math.min(arr.length, 400) * 10 + valueKeys.length,
  };
}

/**
 * Rank value columns and drop the ones that are only rounding residue.
 *
 * Real example, simulate_strategy over one day: pv_kw peaks at 4.12 while
 * grid_export_kw peaks at 1.5e-14. Those two are float noise from a simulation
 * that never touched the grid — the model's own reply calls them "numerical
 * rounding" — and plotted they are flat zero lines that each cost a legend
 * entry.
 *
 * The test is relative, not absolute: if every column is tiny they are all kept,
 * because then tiny is the actual scale of the data.
 */
const NOISE_RATIO = 1e-9;

function pickSeriesKeys(arr, valueKeys) {
  const ranked = valueKeys.map((k) => {
    const vals = arr.map((r) => asNumber(r[k])).filter((v) => v !== null);
    const mag = vals.length ? Math.max(...vals.map(Math.abs)) : 0;
    const span = vals.length ? Math.max(...vals) - Math.min(...vals) : 0;
    return { k, mag, rank: span || mag };
  }).sort((a, b) => b.rank - a.rank);

  const biggest = ranked.length ? ranked[0].mag : 0;
  const meaningful = biggest > 0
    ? ranked.filter((x) => x.mag >= biggest * NOISE_RATIO)
    : ranked;
  return (meaningful.length ? meaningful : ranked).slice(0, 6).map((x) => x.k);
}

function shortLabel(v, key) {
  // An `hour` column is 0-23, and a bare "0" on an axis reads as a quantity
  // rather than midnight.
  if (/^hour$/i.test(String(key || '')) && Number.isInteger(Number(v)) && Number(v) >= 0 && Number(v) <= 23) {
    return `${String(v).padStart(2, '0')}:00`;
  }
  return shortLabelRaw(v);
}

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Axis label for one x value.
 *
 * Timestamps come back as UTC with a Z — `ts: "2026-08-25T22:30:00.000Z"` — and
 * the previous version sliced "22:30" straight out of the string. That put the
 * axis ten hours behind in Sydney, which is what made a mid-morning forecast
 * read as a late-night one. Parse the instant and render it in the viewer's own
 * timezone, which is what the platform's own charts do.
 *
 * A date-only value is deliberately NOT converted. "2026-08-24" is a calendar
 * date, not an instant; parsing it gives UTC midnight, which lands on the
 * previous day for anyone behind UTC.
 */
function shortLabelRaw(v) {
  const str = String(v == null ? '' : v);
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(str);
  if (iso) {
    if (!iso[4]) return `${iso[2]}-${iso[3]}`;
    const d = new Date(str);
    if (!Number.isNaN(d.getTime())) return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    return `${iso[4]}:${iso[5]}`;
  }
  return str.length > 14 ? `${str.slice(0, 13)}…` : str;
}

/** Full local timestamp, for the tooltip, so an axis reading 08:30 is checkable. */
function fullLabel(v) {
  const str = String(v == null ? '' : v);
  if (!/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(str)) return str;
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? str : d.toLocaleString();
}

/**
 * Turn one row of numbers into a categorical series, one bar per field.
 *
 * A single-day simulation returns `daily` and `bands` with exactly one row each,
 * plus a `totals` object — none of which is a series, so all three would render
 * as nothing. Transposed, `totals` is the most legible summary of the whole
 * payload: pv 24.2, load 13.3, charge 23.1, discharge 12.3 side by side.
 *
 * At least three numeric fields are required, which is what separates a summary
 * worth drawing from incidental config: `period` (n_steps, step_minutes) and
 * `retail` (two rates) have two apiece and stay out.
 */
const TRANSPOSE_MIN_FIELDS = 3;

/**
 * A field name as an axis label. The unit suffix is the least informative part
 * and every field in a set tends to share it, so it goes first — without that,
 * battery_charge_kwh and battery_discharge_kwh both truncate to "battery_charg…"
 * and read as the same bar.
 */
const fieldLabel = (k) => String(k)
  .replace(/_(aud_per_kwh|kwh|kw|aud|pct|percent)$/i, '')
  .replace(/_/g, ' ')
  .trim()
  .slice(0, 20);

function describeRowAsCategories(row, path) {
  if (!isPlainRow(row)) return null;
  const numeric = Object.keys(row).filter((k) => asNumber(row[k]) !== null);
  if (numeric.length < TRANSPOSE_MIN_FIELDS) return null;

  const rows = numeric.map((k) => ({ __label: fieldLabel(k), value: asNumber(row[k]) }));
  const biggest = Math.max(...rows.map((r) => Math.abs(r.value)));
  const kept = biggest > 0 ? rows.filter((r) => Math.abs(r.value) >= biggest * NOISE_RATIO) : rows;
  if (kept.length < 2) return null;

  return {
    kind: 'category',
    path,
    labelKey: 'field',
    seriesKeys: ['value'],
    rows: kept,
    transposed: true,
    // Below every real series: this is a fallback view, not the preferred one.
    score: kept.length,
  };
}

/**
 * Every chartable array in a tool result, best first.
 * Depth and node count are bounded: a result is arbitrary JSON from a tool we do
 * not control, and this runs on every render.
 */
function findSeries(result, maxNodes = 400) {
  const found = [];
  let seen = 0;
  const walk = (node, path, depth) => {
    if (seen++ > maxNodes || depth > 5 || !node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      const described = describeArray(node, path || 'result');
      if (described) { found.push(described); return; }
      // A one-row array is not a series, but its single row may still be a
      // summary worth drawing as bars.
      if (node.length === 1) {
        const t = describeRowAsCategories(node[0], path || 'result');
        if (t) found.push(t);
        return;
      }
      // An array of arrays is not something we chart, but an array of objects
      // may hold nested series worth reaching.
      for (const child of node.slice(0, 4)) walk(child, path, depth + 1);
      return;
    }
    // A plain object of numbers (totals, summary) is a candidate in its own right
    if (path) {
      const t = describeRowAsCategories(node, path);
      if (t) found.push(t);
    }
    for (const k of Object.keys(node)) walk(node[k], path ? `${path}.${k}` : k, depth + 1);
  };
  walk(result, '', 0);
  // A one-day simulation's `daily` row and its `totals` object hold the same
  // numbers, so transposing both yields two identical charts. Keep the first.
  const signatures = new Set();
  const unique = found.filter((f) => {
    const sig = `${f.kind}|${f.seriesKeys.join(',')}|${f.rows.map((r) => `${r.__label}=${r[f.seriesKeys[0]]}`).join(';')}`;
    if (signatures.has(sig)) return false;
    signatures.add(sig);
    return true;
  });
  return unique.sort((a, b) => b.score - a.score);
}

/**
 * Read an SSE body and hand each frame to `onEvent`.
 *
 * fetch rather than EventSource: EventSource cannot set an Authorization header,
 * and every call here carries this app's bearer token.
 *
 * Frames are split on a blank line, and a partial frame is carried over — a
 * chunk boundary lands mid-frame often enough that not doing this drops events.
 * Lines beginning ':' are keepalive comments and carry no data.
 */
async function readSse(response, onEvent, shouldStop) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    if (shouldStop && shouldStop()) { reader.cancel(); break; }
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop();
    for (const frame of frames) {
      const line = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      try { onEvent(JSON.parse(line.slice(5).trim())); } catch { /* partial or comment */ }
    }
  }
}

const PRE = {
  fontSize: 10.5, background: '#f4f6f8', border: '1px solid #eceff2', borderRadius: 4,
  padding: 6, margin: 0, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};

const TOOL_STATE = {
  running: { icon: <LoadingOutlined spin />, colour: '#54A2CC', label: 'running' },
  ok: { icon: <CheckCircleFilled />, colour: '#05A677', label: 'ok' },
  error: { icon: <CloseCircleFilled />, colour: '#FA5252', label: 'error' },
};

/** One tool call: what was asked, and what came back. */
function ToolBlock({ step }) {
  const [open, setOpen] = useState(false);
  const state = step.error ? 'error' : (step.result === undefined ? 'running' : 'ok');
  const meta = TOOL_STATE[state];
  return (
    <div style={{
      border: '1px solid #e8ecf1', borderLeft: `3px solid ${meta.colour}`,
      borderRadius: 5, background: '#fbfcfd', margin: '6px 0', fontSize: 12,
    }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', cursor: 'pointer' }}
      >
        <CaretRightOutlined rotate={open ? 90 : 0} style={{ fontSize: 10, color: '#999' }} />
        <ToolOutlined style={{ color: meta.colour }} />
        <code style={{ fontSize: 11 }}>{step.name}</code>
        <span style={{ color: meta.colour, fontSize: 11 }}>{meta.icon}</span>
        <span style={{ flex: 1 }} />
        {step.duration_ms != null && (
          <span style={{ color: '#aaa', fontSize: 10 }}>{fmtMs(step.duration_ms)}</span>
        )}
      </div>
      {open && (
        <div style={{ padding: '0 8px 8px 26px' }}>
          <div style={{ color: '#888', fontSize: 10, margin: '2px 0' }}>input</div>
          <pre style={{ ...PRE, maxHeight: 130 }}>{JSON.stringify(step.input ?? {}, null, 1)}</pre>
          {state !== 'running' && (
            <>
              <div style={{ color: '#888', fontSize: 10, margin: '6px 0 2px' }}>
                {step.error ? 'error' : 'result'}
              </div>
              <pre style={{ ...PRE, maxHeight: 200 }}>
                {step.error
                  ? String(step.error)
                  : JSON.stringify(step.result, null, 1).slice(0, 6000)}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * The model's reasoning: grey and folded away once the turn is over, but opened
 * while it streams, because watching it arrive is the only visible sign that a
 * long turn is progressing. The final answer is NOT streamed — it lands in one
 * frame at the end — so without this a slow turn would look like a hung page.
 */
function ReasoningBlock({ text, streaming }) {
  const [open, setOpen] = useState(false);
  const wasStreaming = useRef(false);
  useEffect(() => {
    if (streaming) { setOpen(true); wasStreaming.current = true; }
    else if (wasStreaming.current) { setOpen(false); wasStreaming.current = false; }
  }, [streaming]);
  const tailRef = useRef(null);
  useEffect(() => {
    if (open && streaming && tailRef.current) tailRef.current.scrollTop = tailRef.current.scrollHeight;
  }, [text, open, streaming]);
  if (!text) return null;
  return (
    <div style={{ margin: '2px 0 6px' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
          color: '#9aa4ae', fontSize: 11, userSelect: 'none',
        }}
      >
        <CaretRightOutlined rotate={open ? 90 : 0} style={{ fontSize: 9 }} />
        <BulbOutlined />
        <span>{streaming ? 'thinking…' : `reasoning · ${text.length} chars`}</span>
      </div>
      {open && (
        <div
          ref={tailRef}
          style={{
            marginTop: 4, padding: '6px 9px', maxHeight: 150, overflowY: 'auto',
            background: '#f7f8fa', borderLeft: '2px solid #e2e6ea', borderRadius: 3,
            color: '#8b949e', fontSize: 11.5, lineHeight: 1.55, whiteSpace: 'pre-wrap',
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
}

const LINE_COLOURS = ['#54A2CC', '#f5b759', '#05A677', '#845EC2', '#FA5252', '#3C4858'];

function ResultChart({ step }) {
  const options = useMemo(() => findSeries(step?.result), [step]);
  const [pick, setPick] = useState(0);
  // A new tool result resets the choice; the old index may not even exist.
  useEffect(() => { setPick(0); }, [step]);

  if (!step) {
    return <Empty description="Send a message — charts appear when a tool returns data worth plotting" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }
  if (!options.length) {
    return (
      <div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
          <b>{step.name}</b> returned nothing chartable — no array of rows with a label and a number.
        </div>
        <pre style={{ fontSize: 11, background: '#fafafa', padding: 8, maxHeight: 220, overflow: 'auto', margin: 0 }}>
          {JSON.stringify(step.result, null, 1).slice(0, 4000)}
        </pre>
      </div>
    );
  }

  const series = options[Math.min(pick, options.length - 1)];
  const isBar = series.kind === 'category';
  const Chart = isBar ? BarChart : LineChart;

  return (
    <>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span>
          <b>{step.name}</b>
          {' · '}
          <code style={{ fontSize: 11 }}>{series.path}</code>
          {' · '}{series.rows.length} {isBar ? 'categories' : 'points'}
          {series.transposed ? ' (one row, shown by field)' : ''}
          {series.kind === 'time' && !series.transposed && (
            <span style={{ color: '#aaa' }}>
              {' · '}times in {Intl.DateTimeFormat().resolvedOptions().timeZone || 'your local time'}
            </span>
          )}
        </span>
        {/* One result often holds several plottable arrays — simulate_strategy
            returns daily totals and tariff bands. Offer them rather than
            silently picking one. */}
        {options.length > 1 && options.map((o, i) => (
          <Tag
            key={o.path}
            color={i === (pick < options.length ? pick : 0) ? 'blue' : 'default'}
            style={{ cursor: 'pointer', margin: 0, fontSize: 10 }}
            onClick={() => setPick(i)}
          >
            {o.path.split('.').pop()}
          </Tag>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={190}>
        <Chart data={series.rows} margin={{ top: 5, right: 8, bottom: 0, left: -12 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="__label" tick={{ fontSize: 10 }} interval="preserveStartEnd" minTickGap={isBar ? 4 : 28} />
          <YAxis tick={{ fontSize: 10 }} width={52} />
          <ReTooltip
            contentStyle={{ fontSize: 11 }}
            labelFormatter={(label, payload) => (payload && payload[0] && payload[0].payload.__full) || label}
          />
          {!series.transposed && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {series.seriesKeys.map((k, i) => (isBar
            ? <Bar key={k} dataKey={k} fill={LINE_COLOURS[i % LINE_COLOURS.length]} />
            : <Line key={k} type="monotone" dataKey={k} stroke={LINE_COLOURS[i % LINE_COLOURS.length]}
                strokeWidth={2} dot={false} />))}
        </Chart>
      </ResponsiveContainer>
    </>
  );
}

function AiAssistantTab({ initialSn, initialNmi }) {
  const [sn, setSn] = useState('');
  const [snInput, setSnInput] = useState('');
  const [snResults, setSnResults] = useState([]);
  const [snSearching, setSnSearching] = useState(false);
  const [snCapped, setSnCapped] = useState(false);
  const [snLookupDown, setSnLookupDown] = useState(false);
  const [nmiCandidates, setNmiCandidates] = useState(null);
  const [bundling, setBundling] = useState(false);
  const [bundleNote, setBundleNote] = useState(null);
  // Not state: there is no control that changes it. See PLAN above.
  const plan = PLAN;
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);      // { role, text, toolSteps, usage, durationMs, error }
  const [toolHistory, setToolHistory] = useState([]); // flat, newest last
  const [usage, setUsage] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [chartStep, setChartStep] = useState(null);

  const [input, setInput] = useState('');
  // The customer's own system and tariffs, and anything attached to this turn.
  const [profile, setProfile] = useState(null);       // { identity, bundle }
  const [profileLoading, setProfileLoading] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{ attachmentId, filename, size }]
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dragDepth = useRef(0);
  const [sending, setSending] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState(null);
  const [serviceDown, setServiceDown] = useState(false);
  const chatRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    api.get('/api/ai-assistant/catalog')
      .then((r) => { setCatalog(r.data.data); setServiceDown(false); })
      .catch(() => setServiceDown(true));
  }, []);

  // Searches the assistant platform itself rather than a local list: it is the
  // only authoritative source of valid SNs. The same call will accept a NMI once
  // the platform supports it, so nothing here needs to change for that.
  useEffect(() => {
    const q = snInput.trim();
    if (q.length < 2) { setSnResults([]); setSnCapped(false); return undefined; }
    let cancelled = false;
    setSnSearching(true);
    const timer = setTimeout(async () => {
      try {
        const r = await api.get('/api/ai-assistant/search', { params: { q } });
        if (cancelled) return;
        setSnResults(r.data.data.results || []);
        setSnCapped(!!r.data.data.capped);
        setSnLookupDown(r.data.data.match === 'unavailable');
      } catch {
        if (!cancelled) { setSnResults([]); setSnCapped(false); }
      }
      if (!cancelled) setSnSearching(false);
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); setSnSearching(false); };
  }, [snInput]);

  const refreshUsage = useCallback(() => {
    api.get('/api/ai-assistant/usage').then((r) => setUsage(r.data.data)).catch(() => {});
  }, []);
  useEffect(() => { refreshUsage(); }, [refreshUsage]);

  // Follow the tail only while the reader is already at the bottom. Yanking the
  // view down mid-stream while someone is reading further up is worse than not
  // following at all.
  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  // Opening a session is find-or-create on (user, sn) at both ends, so coming
  // back to an SN resumes the same conversation. The provider used to be part of
  // that key and is not any more.
  const openSession = useCallback(async (target, targetPlan, { byNmi = false } = {}) => {
    if (!target) return;
    setOpening(true); setError(null); setNmiCandidates(null);
    try {
      const r = await api.post('/api/ai-assistant/session', {
        ...(byNmi ? { nmi: target } : { sn: target }),
        plan: targetPlan,
      });
      setSession(r.data.data);
      // by-nmi answers with the SN it resolved to, which is what the box shows
      setSn(r.data.data.sn || target);
      setSnInput(r.data.data.sn || target);
      const h = await api.get(`/api/ai-assistant/sessions/${r.data.data.sessionRowId}/history`);
      const turns = h.data.data.turns || [];
      const rebuilt = [];
      const steps = [];
      for (const t of turns) {
        rebuilt.push({ role: 'user', text: t.prompt });
        rebuilt.push({
          role: 'assistant', text: t.reply_text, error: t.error,
          toolSteps: t.tool_steps || [],
          reasoning: t.reasoning,
          usage: { inputTokens: t.input_tokens, outputTokens: t.output_tokens, costAud: t.cost_aud },
          model: t.model,
          routerTier: t.router_tier,
          durationMs: t.duration_ms,
        });
        for (const s of (t.tool_steps || [])) steps.push({ ...s, at: t.created_at });
      }
      setMessages(rebuilt);
      setToolHistory(steps);
      const lastPlottable = [...steps].reverse().find((s) => findSeries(s.result).length);
      setChartStep(lastPlottable || steps[steps.length - 1] || null);
      setServiceDown(false);
    } catch (err) {
      const body = err.response?.data || {};
      // One NMI can front several inverters on an embedded network. That is a
      // question for the user, not a failure, so the candidates are offered.
      if (body.code === 'AMBIGUOUS_NMI' && Array.isArray(body.candidates)) {
        setNmiCandidates(body.candidates);
        setError(body.message);
      } else {
        setError(body.message || 'Failed to open the session');
      }
      if (err.response?.status === 502 || err.response?.status === 503) setServiceDown(true);
    }
    setOpening(false);
  }, []);

  // A NMI handed over from the NMI Meter Data tab opens straight away. The ref
  // guard means a re-render, or the user switching to another SN by hand, does
  // not drag them back to the handed-over one.
  const openedForRef = useRef(null);
  useEffect(() => {
    const nmi = String(initialNmi || '').trim();
    const target = nmi || String(initialSn || '').trim();
    if (!target || openedForRef.current === target) return;
    openedForRef.current = target;
    setSnInput(target);
    // A value handed over from the meter list is a NMI, not a serial. The
    // service resolves NMI -> SN itself, so it goes down the by-nmi path.
    openSession(target, plan, { byNmi: !!nmi });
  }, [initialNmi, initialSn, plan, openSession]);

  // Most tools answer "no bundle" for an SN that has never been provisioned.
  // Upstream is idempotent, so this is safe to offer as a button rather than
  // something the user has to reason about first.
  const provisionBundle = useCallback(async () => {
    if (!session || bundling) return;
    setBundling(true); setBundleNote(null); setError(null);
    try {
      const r = await api.post(`/api/ai-assistant/sessions/${session.sessionRowId}/bundle`, {});
      const payload = r.data.data.result || {};
      const inner = payload.result || payload;
      // The tool answers { outcome: 'already_exists' | 'created', bundle: {...} }
      const b = inner.bundle || {};
      const cluster = b.cluster ? ` · cluster ${b.cluster}` : '';
      setBundleNote({
        ok: true,
        text: inner.outcome === 'already_exists'
          ? `${session.sn} already has a bundle (v${b.version ?? '?'}${cluster}) — nothing changed.`
          : `Bundle provisioned for ${session.sn}${cluster}.`,
      });
      setToolHistory((prev) => [...prev, {
        name: 'create_bundle_for_sn', input: { sn: session.sn }, result: inner,
        duration_ms: r.data.data.durationMs, at: new Date().toISOString(),
      }]);
    } catch (err) {
      setBundleNote({ ok: false, text: err.response?.data?.message || 'Bundle provisioning failed' });
    }
    setBundling(false);
  }, [session, bundling]);

  /**
   * The customer's system and tariffs.
   *
   * Two tools because the platform splits them: identify_customer_by_sn returns
   * the ratings recorded against the site in np_system, and get_bundle_profile
   * returns what has been linked to the SN's bundle — the retail tariff, the
   * DNSP and the control policy. Neither is a substitute for the other.
   *
   * Failures are kept rather than thrown: one of the two answering is more
   * useful than an empty panel, and a site with no bundle is the normal state
   * before anyone has uploaded a bill, not an error.
   */
  /**
   * One call: the server assembles it from whichever sources answer.
   *
   * It was two direct tool calls from here, which worked from a laptop and
   * failed in production — the deployed backend's egress IP is classified
   * cross_site by the platform and confined to /api/assistant/plans/*, so the
   * tool endpoint those calls use is refused outright. The server now says
   * which pieces it could get and why the rest are missing.
   */
  const loadProfile = useCallback(async (sn, nmi) => {
    if (!sn && !nmi) return;
    setProfileLoading(true);
    try {
      const r = await api.get('/api/ai-assistant/system', { params: { sn, nmi }, timeout: 60000 });
      setProfile(r.data.data);
    } catch (err) {
      setProfile({ loadError: err.response?.data?.message || 'Could not read the system details' });
    }
    setProfileLoading(false);
  }, []);

  useEffect(() => {
    if (!session?.sn) { setProfile(null); return; }
    loadProfile(session.sn, session.nmi);
  }, [session, loadProfile]);

  /**
   * Attach files to this turn.
   *
   * The upload is its own step, not part of sending: the platform hands back an
   * attachment_id that the model needs in order to call parse_electricity_bill,
   * so the file has to be up before the message that refers to it goes out.
   *
   * The body is the file itself. A multipart form would have to survive being
   * rebuilt by this app and forwarded again, and a mangled boundary shows up as
   * an empty upload with nothing explaining it.
   */
  const uploadFiles = useCallback(async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    if (!session) { message.warning('Open a session before attaching a file'); return; }

    setUploading(true);
    for (const file of list) {
      if (file.size > MAX_UPLOAD_BYTES) {
        message.error(`${file.name} is ${fmtBytes(file.size)} — the limit is 15 MB`);
        continue;
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await api.post(
          `/api/ai-assistant/sessions/${session.sessionRowId}/attachments`,
          file,
          {
            params: { filename: file.name },
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            timeout: 120000,
          },
        );
        setAttachments((prev) => [...prev, r.data.data]);
      } catch (err) {
        message.error(err.response?.data?.message || `Could not upload ${file.name}`);
      }
    }
    setUploading(false);
  }, [session]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    uploadFiles(e.dataTransfer?.files);
  }, [uploadFiles]);

  // Counted rather than toggled: dragging over a child fires dragleave on the
  // parent, so a boolean flickers the overlay off mid-drag.
  const onDragEnter = useCallback((e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    dragDepth.current += 1;
    setDragging(true);
  }, []);
  const onDragLeave = useCallback(() => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  }, []);

  // Streaming turn. The events arrive as route -> reasoning* -> tool_start ->
  // tool_end -> text -> done, so the assistant bubble is built up in place: the
  // reasoning streams live, tool calls appear as they run, and the answer lands
  // whole at the end (the platform sends `text` in one frame, not token by
  // token). Falls back to the blocking endpoint if the stream cannot open.
  const send = useCallback(async () => {
    const typed = input.trim();
    if ((!typed && !attachments.length) || !session || sending) return;

    /**
     * The attachment ids travel in the message.
     *
     * parse_electricity_bill takes an attachment_id, and the model has no other
     * way to learn one — the upload happened over a side channel it never saw.
     * Naming them in the text is what makes the file usable, and it is also
     * what the transcript should show: a turn that acted on a file the reader
     * cannot see is a turn nobody can check.
     */
    const refs = attachments
      .map((a) => `- ${a.filename} (attachment_id: ${a.attachmentId})`)
      .join('\n');
    const text = attachments.length
      ? `${typed || 'Please read the attached bill.'}\n\nAttached file${attachments.length > 1 ? 's' : ''}:\n${refs}`
      : typed;

    setInput('');
    setAttachments([]);
    setSending(true); setError(null);

    const controller = new AbortController();
    abortRef.current = controller;
    // The placeholder is appended once and then mutated by index, so every event
    // does not rebuild the whole list.
    let index = -1;
    setMessages((prev) => {
      const next = [...prev, { role: 'user', text }, { role: 'assistant', streaming: true, reasoning: '', text: '', toolSteps: [] }];
      index = next.length - 1;
      return next;
    });
    const patch = (fn) => setMessages((prev) => {
      if (index < 0 || !prev[index]) return prev;
      const next = [...prev];
      next[index] = fn(next[index]);
      return next;
    });

    const started = Date.now();
    let fellBackToBlocking = false;
    try {
      const res = await fetch(`/api/ai-assistant/sessions/${session.sessionRowId}/message/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ text }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) throw new Error(`stream unavailable (${res.status})`);

      const stamped = [];
      await readSse(res, (ev) => {
        if (ev.type === 'route') {
          patch((m) => ({ ...m, routerTier: ev.tier, routerReason: ev.reason, model: ev.model }));
        } else if (ev.type === 'reasoning') {
          patch((m) => ({ ...m, reasoning: (m.reasoning || '') + (ev.delta || '') }));
        } else if (ev.type === 'text') {
          patch((m) => ({ ...m, text: (m.text || '') + (ev.delta || '') }));
        } else if (ev.type === 'tool_start') {
          patch((m) => ({ ...m, toolSteps: [...(m.toolSteps || []), { ...(ev.step || {}) }] }));
        } else if (ev.type === 'tool_end') {
          const step = { ...(ev.step || {}), at: new Date().toISOString() };
          patch((m) => {
            const steps = [...(m.toolSteps || [])];
            const i = steps.findIndex((x) => x.id === step.id);
            if (i >= 0) steps[i] = { ...steps[i], ...step }; else steps.push(step);
            return { ...m, toolSteps: steps };
          });
          stamped.push(step);
          // Chart it the moment the result lands rather than after the turn
          setToolHistory((prev) => [...prev, step]);
          if (findSeries(step.result).length) setChartStep(step);
          else setChartStep((prev) => prev || step);
        } else if (ev.type === 'done') {
          patch((m) => ({
            ...m,
            text: ev.text || m.text,
            toolSteps: (ev.tool_steps && ev.tool_steps.length) ? ev.tool_steps : m.toolSteps,
          }));
        } else if (ev.type === 'recorded') {
          // Our own trailing frame: the costed usage, so the client prices nothing
          patch((m) => ({ ...m, streaming: false, usage: ev.usage, durationMs: ev.durationMs }));
        } else if (ev.type === 'error') {
          patch((m) => ({ ...m, streaming: false, error: ev.message || 'the assistant reported an error' }));
          setError(ev.message || 'The assistant reported an error');
        }
      }, () => controller.signal.aborted);

      patch((m) => (m.streaming
        ? { ...m, streaming: false, durationMs: m.durationMs ?? Date.now() - started }
        : m));
    } catch (err) {
      if (err.name === 'AbortError') {
        patch((m) => ({ ...m, streaming: false, stopped: true }));
      } else {
        fellBackToBlocking = true;
      }
    }

    if (fellBackToBlocking) {
      try {
        const r = await api.post(`/api/ai-assistant/sessions/${session.sessionRowId}/message`, { text });
        const d = r.data.data;
        patch((m) => ({
          ...m,
          streaming: false,
          text: d.text,
          toolSteps: d.toolSteps || [],
          usage: d.usage,
          durationMs: d.durationMs,
          model: d.model,
          routerTier: d.router && d.router.tier,
          routerReason: d.router && d.router.reason,
        }));
        const stamped = (d.toolSteps || []).map((x) => ({ ...x, at: new Date().toISOString() }));
        setToolHistory((prev) => [...prev, ...stamped]);
        const plottable = [...stamped].reverse().find((x) => findSeries(x.result).length);
        if (plottable) setChartStep(plottable);
        else if (stamped.length) setChartStep(stamped[stamped.length - 1]);
      } catch (err2) {
        const msg = err2.response?.data?.message || 'The assistant call failed';
        patch((m) => ({ ...m, streaming: false, error: msg }));
        setError(msg);
      }
    }

    abortRef.current = null;
    setSending(false);
    refreshUsage();
  }, [input, attachments, session, sending, refreshUsage]);

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  // Suggestions come from the platform search. monitorNmis is deliberately not
  // used here: those are NMIs, and the session key is a system serial number —
  // offering them as SNs produced sessions no SN-scoped tool could serve.
  const snOptions = useMemo(() => snResults.map((r) => ({
    value: r.sn,
    label: (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontFamily: 'monospace' }}>{r.sn}</span>
        <span style={{ color: '#999', fontSize: 11 }}>
          {r.nmi ? `NMI ${r.nmi}` : ''}{r.state ? ` · ${r.state}` : ''}{r.exact ? ' · exact' : ''}
        </span>
      </div>
    ),
  })), [snResults]);

  // Summed on the client from what is on screen, so it tracks the conversation
  // the user is looking at rather than the session row's lifetime total.
  const sessionCost = useMemo(
    () => messages.reduce((a, m) => a + (Number(m.usage?.costAud) || 0), 0),
    [messages],
  );

  const toolColumns = [
    { title: '#', width: 46, render: (_, __, i) => i + 1 },
    {
      title: 'Tool', dataIndex: 'name', width: 210,
      render: (v) => <code style={{ fontSize: 11, background: '#f5f5f5', padding: '1px 4px' }}>{v}</code>,
    },
    {
      title: 'Status', width: 80,
      render: (_, r) => r.error
        ? <Tag color="error">error</Tag>
        : <Tag color="success">ok</Tag>,
    },
    { title: 'Took', dataIndex: 'duration_ms', width: 80, render: (v) => fmtMs(v) },
    { title: 'When', width: 90, render: (_, r) => clockOf(r.at) },
    {
      title: '', width: 60,
      render: (_, r) => (
        <Tooltip title="Show this result in the chart above">
          <Button size="small" type="link" onClick={() => setChartStep(r)}>chart</Button>
        </Tooltip>
      ),
    },
  ];

  const llmEvents = messages.filter((m) => m.role === 'assistant');

  const ident = profile?.identity || null;
  const bundle = profile?.bundle || null;
  const network = profile?.network || null;
  const retailTariff = pick(bundle, ['retail_tariff', 'retailTariff', 'retail', 'tariff_retail']);
  const dnspTariff = pick(bundle, ['dnsp_tariff', 'dnspTariff', 'network_tariff', 'networkTariff']);
  const bundleMissing = bundle && bundle.bundle_exists === false;

  /**
   * The one failure worth explaining rather than reporting.
   *
   * The platform's own message names the file and the flag to change, which is
   * a note to its maintainers and nothing an operator can act on — so it is
   * replaced with what this means here, and what unblocks it.
   */
  const restricted = profile?.identityBlocked === 'restricted' || profile?.bundleBlocked === 'restricted';
  const restrictedNote = (
    <Alert
      type="warning" showIcon style={{ marginTop: 8 }}
      message="Ratings and retail tariff cannot be read from here yet"
      description={(
        <span style={{ fontSize: 12 }}>
          The assistant platform classifies this portal as a cross-site caller and allows it only the
          plan endpoints, so the lookup that carries PV, battery, inverter and the linked retail tariff
          is refused. Chat is unaffected — the assistant can still answer these questions itself, because
          its own tool calls run inside the platform. Lifting it is a change on the platform side
          (the portal&apos;s IP category), which also restores the SN search box.
        </span>
      )}
    />
  );

  const tariffBlock = (label, obj, emptyHint) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>{label}</div>
      {obj ? (
        <table style={{ fontSize: 12, width: '100%' }}>
          <tbody>
            {scalarRows(obj).map(([k, v]) => (
              <tr key={k}>
                <td style={{ color: '#888', padding: '2px 10px 2px 0', whiteSpace: 'nowrap' }}>{k}</td>
                <td style={{ wordBreak: 'break-word' }}>{String(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{ fontSize: 12, color: '#999' }}>{emptyHint}</div>
      )}
    </div>
  );

  /**
   * The customer's own system and tariffs.
   *
   * This is what somebody answering a question about a site needs in front of
   * them, so it is what this panel opens on now. The tool-call and prompt
   * diagnostics that used to be the whole of it answer a developer's question
   * rather than an operator's, and sit behind it.
   *
   * The tariff halves are frequently empty, and that is the truth rather than a
   * fault: a tariff exists here only once somebody has uploaded the bill and had
   * it parsed, which is what the paperclip in the chat box is for. The empty
   * state says which step is missing instead of leaving a blank box.
   */
  const systemPanel = (
    <Spin spinning={profileLoading}>
      {!session ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Open a session to see the customer's system" />
      ) : (
        <>
          <Descriptions
            size="small" column={2} bordered
            styles={{ label: { width: 88, fontSize: 12 }, content: { fontSize: 12 } }}
            items={[
              { key: 'pv', label: 'PV', children: num(ident?.pv_kw, 'kW') },
              { key: 'bat', label: 'Battery', children: num(ident?.battery_kwh, 'kWh') },
              { key: 'inv', label: 'Inverter', children: num(ident?.inverter_kw, 'kW') },
              { key: 'status', label: 'Status', children: orGap(ident?.status) },
              { key: 'nmi', label: 'NMI', children: orGap(ident?.nmi) },
              { key: 'region', label: 'Region', children: orGap(ident?.region) },
              { key: 'cluster', label: 'Cluster', children: orGap(ident?.cluster_name) },
              { key: 'dnsp', label: 'DNSP', children: orGap(ident?.dnsp_code) },
            ]}
          />
          {profile?.loadError && (
            <Alert type="error" showIcon style={{ marginTop: 8 }} message={profile.loadError} />
          )}
          {restricted && restrictedNote}
          {!restricted && profile?.identityBlocked && (
            <Alert
              type="warning" showIcon style={{ marginTop: 8 }}
              message={`System lookup unavailable: ${profile.identityReason || ''}`}
            />
          )}
          {ident && ident.pv_kw == null && (
            <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>
              Ratings come from the platform&apos;s np_system record. A gap there is a gap in that
              record, not a failure here.
            </div>
          )}

          <Divider style={{ margin: '12px 0 10px' }} />

          <div style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
              Network (DNSP) tariff
              <span style={{ fontWeight: 400, color: '#999', marginLeft: 6, fontSize: 11 }}>
                from the Powow Power warehouse
              </span>
            </div>
            {profile?.networkError && (
              <div style={{ fontSize: 12, color: '#c0392b' }}>{profile.networkError}</div>
            )}
            {!profile?.networkError && network && !network.notMapped && (
              <Descriptions
                size="small" column={1} bordered
                styles={{ label: { width: 140, fontSize: 12 }, content: { fontSize: 12 } }}
                items={[
                  { key: 'tc', label: 'Tariff code', children: orGap(network.tariffCode) },
                  { key: 'cp', label: 'Connection point', children: orGap(network.connectionPoint) },
                ]}
              />
            )}
            {!profile?.networkError && network && network.notMapped && (
              <div style={{ fontSize: 12, color: '#999' }}>
                NMI {network.nmi} has no row in the transmission mapping, so no network tariff is
                recorded for it.
              </div>
            )}
            {!profile?.networkError && !network && (
              <div style={{ fontSize: 12, color: '#999' }}>
                No NMI known for this SN yet, so the network tariff cannot be looked up.
              </div>
            )}
            {dnspTariff && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>Linked in the assistant bundle</div>
                <table style={{ fontSize: 12, width: '100%' }}>
                  <tbody>
                    {scalarRows(dnspTariff).map(([k, v]) => (
                      <tr key={k}>
                        <td style={{ color: '#888', padding: '2px 10px 2px 0', whiteSpace: 'nowrap' }}>{k}</td>
                        <td style={{ wordBreak: 'break-word' }}>{String(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {bundleMissing ? (
            <Alert
              type="info" showIcon
              message="No bundle for this SN yet"
              description={(
                <span style={{ fontSize: 12 }}>
                  Tariffs are held against a bundle. Use <b>Provision bundle</b> above to create one,
                  then attach the customer&apos;s bill with the paperclip and ask the assistant to
                  parse it.
                </span>
              )}
            />
          ) : (
            <>
              {tariffBlock(
                'Retailer tariff',
                retailTariff,
                restricted
                  ? 'Cannot be read from here — see above. Ask the assistant in chat instead.'
                  : 'Not set. Attach the bill with the paperclip and ask the assistant to parse it — it can then save the retail tariff against this SN.',
              )}

            </>
          )}
        </>
      )}
    </Spin>
  );

  return (
    <div>
      {serviceDown && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message="AI service unreachable"
          description="The assistant service did not respond. It is hot-patched in production, so this is usually temporary — the panel keeps working from locally stored history."
        />
      )}
      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError(null)} style={{ marginBottom: 12 }} />}

      <Row gutter={12}>
        {/* ---------------- Chat ---------------- */}
        <Col
          xs={24} lg={13}
          /**
           * Dropping a file anywhere over the chat attaches it.
           *
           * onDragOver has to preventDefault as well, or the browser navigates
           * away to the file instead of letting the drop through — the failure
           * looks like the app closing itself.
           */
          onDragEnter={onDragEnter}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          style={{ position: 'relative' }}
        >
          {dragging && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                background: 'rgba(84, 162, 204, 0.10)',
                border: '2px dashed #54A2CC',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                pointerEvents: 'none',
              }}
            >
              <InboxOutlined style={{ fontSize: 40, color: '#54A2CC' }} />
              <span style={{ fontWeight: 600, color: '#2b6a88' }}>
                {session ? 'Drop the bill here' : 'Open a session first'}
              </span>
              <span style={{ fontSize: 12, color: '#666' }}>PDF or photo, up to 15 MB</span>
            </div>
          )}
          <Card
            size="small"
            style={{ height: PANEL_H }}
            styles={{ body: { height: PANEL_H - 40, display: 'flex', flexDirection: 'column', paddingTop: 8 } }}
            title="Chat"
            extra={(
              <Space size={6}>
                {snLookupDown && (
                  <Tooltip title="The platform's system-lookup endpoint is unreachable, so SN suggestions are off. Typing a full SN still works.">
                    <Tag color="orange" style={{ margin: 0 }}>lookup off</Tag>
                  </Tooltip>
                )}
                {sessionCost > 0 && (
                  <Tooltip title="Estimated spend on this conversation, in AUD, from the platform's own pricing">
                    <Tag color="gold" style={{ margin: 0 }}>{fmtAud(sessionCost)}</Tag>
                  </Tooltip>
                )}
                {session && (
                  <Tooltip title="Provision the ML bundle this SN needs before the bill, forecast and policy tools can answer. Idempotent — pressing it twice is harmless.">
                    <Button size="small" loading={bundling} onClick={provisionBundle}>bundle</Button>
                  </Tooltip>
                )}
              </Space>
            )}
          >
            {/* The scroll area is the positioning context for the banner, which
                floats over the transcript instead of occupying a row in it. As a
                sticky child it wrapped to two lines and pushed the buttons out
                of alignment. */}
            <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
              <div
                style={{
                  position: 'absolute', top: 0, left: 0, right: 8, zIndex: 3,
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 10px',
                  // Translucent rather than solid: the text scrolling underneath
                  // stays faintly visible, so the bar reads as floating above the
                  // conversation rather than clipping it.
                  background: 'rgba(255,255,255,0.88)',
                  backdropFilter: 'blur(6px)',
                  WebkitBackdropFilter: 'blur(6px)',
                  border: '1px solid #e8ecf1',
                  borderRadius: 6,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}
              >
                <span style={{ color: '#888', fontSize: 12, whiteSpace: 'nowrap' }}>Chatting about</span>
                {session ? (
                  <>
                    <Tag color="blue" style={{ fontFamily: 'monospace', margin: 0 }}>{session.sn}</Tag>
                    {session.nmi && (
                      <span style={{ color: '#888', fontSize: 11, whiteSpace: 'nowrap' }}>NMI {session.nmi}</span>
                    )}
                  </>
                ) : (
                  <span style={{ color: '#999', fontSize: 12, whiteSpace: 'nowrap' }}>nothing yet</span>
                )}
                <span style={{ flex: 1 }} />
                <AutoComplete
                  size="small"
                  style={{ width: 168 }}
                  placeholder={session ? 'switch SN…' : 'search SN, e.g. TSM2509'}
                  value={snInput}
                  options={snOptions}
                  onChange={(v) => setSnInput(v || '')}
                  onSelect={(v) => { setSnInput(v); openSession(v, plan); }}
                  notFoundContent={snSearching
                    ? <Spin size="small" />
                    : (snLookupDown
                      ? 'search unavailable — type the full SN'
                      : (snInput.trim().length >= 2 ? 'no match' : null))}
                />
                <Tooltip title={snCapped ? 'Showing the top 8 matches — narrow the prefix' : ''}>
                  <Button
                    size="small" icon={<SwapOutlined />} loading={opening}
                    onClick={() => openSession(snInput.trim(), plan)}
                    disabled={!snInput.trim()}
                  >
                    {session ? 'switch' : 'open'}
                  </Button>
                </Tooltip>
              </div>
              {/* Top padding clears the floating bar so the first message is not
                  born underneath it. */}
              <div ref={chatRef} style={{ height: '100%', overflowY: 'auto', paddingRight: 4, paddingTop: 44 }}>
              {session && !session.planFellBack && session.plan === 'savvy' && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="Savvy plan — can reach the dispatch write tool"
                  description="This plan's tool list includes write_dispatch_command, which writes a real chat_override into the dispatch path. This app refuses to call it directly, but only the platform's whitelist can stop the model reaching for it mid-turn. Use the User plan unless you need the extra tools."
                />
              )}
              {session && session.planFellBack && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message={`Running on the ${PLAN_LABEL[session.plan] || session.plan} plan`}
                  description={`The ${PLAN_LABEL[session.planRequested] || session.planRequested} plan is not built on the assistant platform yet. Savvy can reach write_dispatch_command; this app refuses to call it, but only the platform's own whitelist can stop the model reaching for it mid-turn.`}
                />
              )}
              {bundleNote && (
                <Alert
                  type={bundleNote.ok ? 'success' : 'error'}
                  message={bundleNote.text}
                  closable onClose={() => setBundleNote(null)}
                  style={{ marginBottom: 8 }}
                  banner
                />
              )}
              {nmiCandidates && nmiCandidates.length > 0 && (
                <Alert
                  type="warning"
                  style={{ marginBottom: 8 }}
                  message="That NMI covers more than one system"
                  description={(
                    <Space wrap size="small">
                      {nmiCandidates.map((c) => (
                        <Button
                          key={c.sn} size="small"
                          onClick={() => { setNmiCandidates(null); openSession(c.sn, plan); }}
                        >
                          {c.sn}{c.state ? ` · ${c.state}` : ''}{c.status ? ` · ${c.status}` : ''}
                        </Button>
                      ))}
                    </Space>
                  )}
                />
              )}
              {!session && (
                <Empty description="Choose an SN to open its assistant session" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
              {messages.map((m, i) => m.role === 'user' ? (
                <div key={i} style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                  <div style={{ background: '#2f54eb', color: '#fff', padding: '8px 12px', borderRadius: 8, maxWidth: '80%', fontSize: 13 }}>
                    {m.text}
                  </div>
                </div>
              ) : (
                <div key={i} style={{ marginBottom: 12 }}>
                  {/* In the order the turn actually happened: it reasons, it
                      calls tools, then it answers. The antd Collapse this
                      replaced hid the input behind a click and gave no sign a
                      call was still running. */}
                  <ReasoningBlock text={m.reasoning} streaming={!!m.streaming} />
                  {(m.toolSteps || []).map((step, j) => (
                    <ToolBlock key={step.id || j} step={step} />
                  ))}
                  {m.error ? (
                    <Alert type="error" showIcon message={m.error} style={{ fontSize: 12 }} />
                  ) : (m.streaming && !m.text) ? (
                    // The answer is not streamed — it arrives in one frame at the
                    // end — so between the last tool and that frame there is
                    // nothing to render. Without this the bubble sits empty and
                    // reads as a hung request.
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: '#f2f6fa', padding: '9px 12px', borderRadius: 8,
                      color: '#8b949e', fontSize: 12,
                    }}>
                      <Spin size="small" />
                      {(m.toolSteps || []).some((x) => x.result === undefined && !x.error)
                        ? 'running a tool…'
                        : (m.reasoning ? 'writing the answer…' : 'thinking…')}
                    </div>
                  ) : (
                    <div style={{ background: '#f2f6fa', padding: '10px 12px', borderRadius: 8, fontSize: 13 }}>
                      <Markdown>{m.text}</Markdown>
                      {m.stopped && (
                        <div style={{ color: '#d46b08', fontSize: 11, marginTop: 4 }}>stopped — the turn may still have finished on the platform</div>
                      )}
                      {m.usage && (
                        <div style={{ color: '#999', fontSize: 11, marginTop: 6 }}>
                          {fmtInt(m.usage.inputTokens)} in / {fmtInt(m.usage.outputTokens)} out tokens
                          {m.usage.cacheReadTokens ? ` · ${fmtInt(m.usage.cacheReadTokens)} cached` : ''}
                          {m.usage.llmCalls > 1 ? ` · ${m.usage.llmCalls} model calls` : ''}
                          {m.routerTier && (
                            <>
                              {' · '}
                              <Tooltip title={m.routerReason || TIER_HINT[m.routerTier]}>
                                <Tag
                                  color={TIER_COLOR[m.routerTier]}
                                  style={{ margin: 0, fontSize: 10, lineHeight: '14px', padding: '0 4px' }}
                                >
                                  {m.routerTier}
                                </Tag>
                              </Tooltip>
                            </>
                          )}
                          {m.usage.costAud != null && (
                            <>
                              {' · '}
                              <span style={{ color: '#555', fontWeight: 600 }}>
                                {fmtAud(m.usage.costAud)}
                                {m.usage.costComplete === false ? '+' : ''}
                              </span>
                            </>
                          )}
                          {' · '}{fmtMs(m.durationMs)}
                          {m.model ? ` · ${m.model}` : ''}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              </div>
            </div>

            {/* Attached and waiting to be sent. Shown as its own row so a file
                cannot be forgotten about: it does nothing until a message
                refers to it. */}
            {(attachments.length > 0 || uploading) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10, alignItems: 'center' }}>
                {attachments.map((a) => (
                  <Tag
                    key={a.attachmentId}
                    icon={<PaperClipOutlined />}
                    closable
                    closeIcon={<DeleteOutlined />}
                    onClose={() => setAttachments((prev) => prev.filter((x) => x.attachmentId !== a.attachmentId))}
                    style={{ margin: 0, padding: '2px 8px' }}
                  >
                    {a.filename}
                    <span style={{ color: '#888', marginLeft: 6, fontSize: 11 }}>{fmtBytes(a.size)}</span>
                  </Tag>
                ))}
                {uploading && <Tag icon={<LoadingOutlined />} style={{ margin: 0 }}>uploading…</Tag>}
                {attachments.length > 0 && !uploading && (
                  <span style={{ fontSize: 11, color: '#999' }}>
                    sent with your next message
                  </span>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              {/* A plain file input rather than antd Upload: the request is a raw
                  body, not a multipart form, so Upload's own posting would have
                  to be disabled anyway. */}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                multiple
                style={{ display: 'none' }}
                onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }}
              />
              <Tooltip title={session
                ? 'Attach a bill (PDF or photo) — the assistant can then read the tariffs off it'
                : 'Open a session first'}
              >
                <Button
                  icon={<PaperClipOutlined />}
                  loading={uploading}
                  disabled={!session || sending}
                  onClick={() => fileInputRef.current?.click()}
                />
              </Tooltip>
              <TextArea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={session
                  ? 'Ask about the battery control strategy, or drop a bill in to have it read…'
                  : 'Open a session first'}
                autoSize={{ minRows: 1, maxRows: 4 }}
                disabled={!session || sending}
              />
              {/* A streamed turn can run for minutes on the high tier, so it has
                  to be interruptible. Aborting drops our stream; the platform may
                  still finish the turn, which the bubble says rather than
                  pretending the work was cancelled. */}
              {sending ? (
                <Button danger icon={<StopOutlined />} onClick={stop}>Stop</Button>
              ) : (
                <Button
                  type="primary" icon={<SendOutlined />}
                  onClick={send} disabled={!session || (!input.trim() && !attachments.length)}
                  style={{ background: '#54A2CC', borderColor: '#54A2CC' }}
                >
                  Send
                </Button>
              )}
            </div>
          </Card>
        </Col>

        {/* ---------------- Debug ---------------- */}
        <Col xs={24} lg={11}>
          <Card
            size="small"
            title={<Space size="small"><span>Debug</span><Tag color="purple">{toolHistory.length}</Tag></Space>}
            styles={{ body: { height: CHART_CARD_H - 40, overflow: 'auto' } }}
            extra={
              <Button size="small" icon={<ReloadOutlined />} onClick={() => { refreshUsage(); if (sn) openSession(sn, plan); }}>
                Refresh
              </Button>
            }
            style={{ marginBottom: GAP, height: CHART_CARD_H }}
          >
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
              Tool result
              {chartStep && <Tag color="geekblue" style={{ marginLeft: 8 }}>{chartStep.name}</Tag>}
              {session && <span style={{ color: '#999', fontWeight: 400, fontSize: 11, marginLeft: 6 }}>{session.sn}</span>}
            </div>
            <ResultChart step={chartStep} />
          </Card>

          <Card
            size="small"
            extra={session && (
              <Button
                size="small" icon={<ReloadOutlined />} loading={profileLoading}
                onClick={() => loadProfile(session.sn, session.nmi)}
              >
                Refresh
              </Button>
            )}
            style={{ height: PANEL_H - CHART_CARD_H - GAP }}
            styles={{ body: { height: PANEL_H - CHART_CARD_H - GAP - 24, overflow: 'auto' } }}
          >
            <Tabs
              size="small"
              items={[
                {
                  key: 'system',
                  label: 'System & tariff',
                  children: systemPanel,
                },
                {
                  key: 'tools',
                  label: `Tool history (${toolHistory.length})`,
                  children: (
                    <Table
                      size="small" rowKey={(r, i) => `${r.id || r.name}-${i}`}
                      dataSource={toolHistory} columns={toolColumns}
                      pagination={toolHistory.length > 10 ? { defaultPageSize: 10, showSizeChanger: false } : false}
                      scroll={{ x: 580 }}
                      locale={{ emptyText: 'No tool calls yet' }}
                    />
                  ),
                },
                {
                  key: 'llm',
                  label: `LLM events (${llmEvents.length})`,
                  children: (
                    <Table
                      size="small" rowKey={(_, i) => i}
                      dataSource={llmEvents}
                      pagination={false}
                      locale={{ emptyText: 'No turns yet' }}
                      columns={[
                        { title: '#', width: 40, render: (_, __, i) => i + 1 },
                        { title: 'In', width: 80, align: 'right', render: (_, r) => fmtInt(r.usage?.inputTokens) },
                        { title: 'Out', width: 80, align: 'right', render: (_, r) => fmtInt(r.usage?.outputTokens) },
                        { title: 'Cost', width: 90, align: 'right', render: (_, r) => fmtAud(r.usage?.costAud) },
                        {
                          title: 'Tier', width: 64,
                          render: (_, r) => (r.routerTier
                            ? <Tag color={TIER_COLOR[r.routerTier]} style={{ margin: 0 }}>{r.routerTier}</Tag>
                            : '-'),
                        },
                        { title: 'Tools', width: 60, align: 'right', render: (_, r) => (r.toolSteps || []).length },
                        { title: 'Took', width: 80, render: (_, r) => fmtMs(r.durationMs) },
                        { title: 'Status', render: (_, r) => r.error ? <Tag color="error">error</Tag> : <Tag color="success">ok</Tag> },
                      ]}
                    />
                  ),
                },
                {
                  key: 'session',
                  label: 'Session',
                  children: session ? (
                    <table style={{ fontSize: 12, width: '100%' }}>
                      <tbody>
                        {[
                          ['SN', session.sn],
                          ['Plan served', `${PLAN_LABEL[session.plan] || session.plan} (${session.plan})`],
                          ['Plan requested', session.planRequested === session.plan
                            ? '(same)'
                            : `${PLAN_LABEL[session.planRequested] || session.planRequested} — not available`],
                          ['Routed via', session.llmProvider],
                          ['Mode', session.mode],
                          ['Remote session', session.remoteSessionId],
                          ['Local row', session.sessionRowId],
                          ['x-user sent', session.remoteUser],
                          ['API source seen', session.apiSource],
                          ['Created', session.createdAt ? new Date(session.createdAt).toLocaleString() : '-'],
                        ].map(([k, v]) => (
                          <tr key={k}>
                            <td style={{ color: '#888', padding: '3px 10px 3px 0', whiteSpace: 'nowrap' }}>{k}</td>
                            <td style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{v || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No session" />,
                },
                {
                  key: 'usage',
                  label: 'Token usage',
                  children: (
                    <Row gutter={[12, 12]}>
                      {[
                        { t: 'Turns', v: usage?.turns },
                        { t: 'Input tokens', v: usage?.input_tokens },
                        { t: 'Output tokens', v: usage?.output_tokens },
                        { t: 'Tool calls', v: usage?.tool_calls },
                        { t: 'Failed turns', v: usage?.failed_turns },
                        { t: 'Escalated to high', v: usage?.high_tier_turns },
                      ].map((k) => (
                        <Col span={8} key={k.t}>
                          <Statistic title={k.t} value={fmtInt(k.v)} valueStyle={{ fontSize: 18 }} />
                        </Col>
                      ))}
                      <Col span={8}>
                        <Statistic
                          title="Est. spend"
                          value={fmtAud(usage?.cost_aud)}
                          valueStyle={{ fontSize: 18, color: '#d48806' }}
                        />
                      </Col>
                      <Col span={24}>
                        <div style={{ fontSize: 11, color: '#999' }}>
                          Your own spend across every session, from this app's turn log.
                        </div>
                      </Col>
                    </Row>
                  ),
                },
                {
                  key: 'catalog',
                  label: catalog ? `Tools available (${(catalog.tools || []).length})` : 'Tools available',
                  children: catalog ? (
                    <Table
                      size="small" rowKey="name" dataSource={catalog.tools || []}
                      pagination={{ defaultPageSize: 12, showSizeChanger: false }}
                      columns={[
                        { title: 'Tool', dataIndex: 'name', width: 240, render: (v) => <code style={{ fontSize: 11 }}>{v}</code> },
                        { title: 'Params', width: 70, align: 'right',
                          render: (_, r) => Object.keys(r.input_schema?.properties || {}).length },
                        { title: 'Description', dataIndex: 'description', ellipsis: true },
                      ]}
                    />
                  ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Catalog unavailable" />,
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default AiAssistantTab;
